import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../ui/Section';
import Card from '../ui/Card';
import { projects } from '../../data/projects';
import { Code, Database, LineChart } from 'lucide-react';
import { trackEvent } from '../../hooks/useGoogleAnalytics';
import { useCV } from '../../context/CVContext';

const categories = [
  { id: 'all', name: 'Tutti', icon: null },
  { id: 'web', name: 'Web', icon: <Code size={16} /> },
  { id: 'plc', name: 'PLC & Automazione', icon: <Database size={16} /> },
  { id: 'data', name: 'Dati & Analytics', icon: <LineChart size={16} /> },
];

const Projects: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const { isCVMode } = useCV();

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    trackEvent('Projects', 'Filter Category', categoryId);
  };

  const getCategoryIcon = (category: string) => {
    switch(category) {
      case 'web': return <Code size={16} />;
      case 'plc': return <Database size={16} />;
      case 'data': return <LineChart size={16} />;
      default: return null;
    }
  };

  return (
    <Section 
      id="projects" 
      title="Progetti Principali"
      subtitle="Una selezione dei progetti più significativi che ho realizzato."
      className="bg-gray-50 dark:bg-gray-900"
    >
      {/* Category filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-12">
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center ${
              activeCategory === category.id
                ? 'bg-primary-600 text-white dark:bg-primary-500'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {category.icon && <span className="mr-2">{category.icon}</span>}
            {category.name}
          </button>
        ))}
      </div>

      {/* Projects grid */}
      <AnimatePresence>
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="h-full flex flex-col">
                <div className="group bg-gray-100 dark:bg-gray-800 rounded-md p-4 mb-4 flex items-center justify-center h-40 relative z-10 hover:z-50">
                  {project.imageUrl ? (
                    <img
                      src={project.imageUrl}
                      alt={project.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover rounded-md transition-all duration-300 origin-center group-hover:scale-[1.7] group-hover:object-contain group-hover:drop-shadow-2xl"
                    />
                  ) : (
                    <div className="text-5xl text-primary-500">
                      {project.category === 'web' && <Code size={48} />}
                      {project.category === 'plc' && <Database size={48} />}
                      {project.category === 'data' && <LineChart size={48} />}
                    </div>
                  )}
                </div>
                
                <div className="flex-1 flex flex-col">
                  <div>
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {project.title}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                        {getCategoryIcon(project.category)}
                        <span className="ml-1">{
                          project.category === 'web' ? 'Web' :
                          project.category === 'plc' ? 'PLC' : 'Dati'
                        }</span>
                      </span>
                      {project.createdAt && (() => {
                        const created = new Date(project.createdAt);
                        const sixMonthsLater = new Date(created);
                        sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
                        return new Date() < sixMonthsLater;
                      })() && (
                        <span className="relative inline-flex items-center">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60"></span>
                          <span className="relative inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-500 text-white shadow-lg shadow-green-500/50 animate-pulse">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-white"></span>
                            Nuovo
                          </span>
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      {project.description}
                    </p>
                  </div>
                  
                  <div className="mt-auto">
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs font-medium text-gray-800 dark:text-gray-300"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* ====== BLOCCO AGGIUNTO PER IL BOTTONE DEMO ====== */}
                    {project.addHtml && (isCVMode || !project.cvModeOnly) ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: project.addHtml }}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.tagName === 'BUTTON') {
                            trackEvent('Projects', 'Open Demo', project.title);
                          }
                        }}
                      />
                    ) : project.cvModeOnly && (
                      <div className="w-full mt-4 px-4 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-center text-sm text-gray-400 dark:text-gray-500 italic">
                        solo da curriculum
                      </div>
                    )}
                    {/* =================================================== */}

                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </Section>
  );
};

export default Projects;